import { useCallback } from "react";

import { combineReducers, Middleware } from "@reduxjs/toolkit";

import authSlice from "./authSlice";
import { transactionsApi } from "./transactionsApi";
import { oraclePricesApi, useOraclePricesApi } from "./oraclePricesApi";
import { pointsApi, usePointsApi } from "./pointsApi";
import { twitterPointsApi, useTwitterPointsApi } from "./twitterPointsApi";
import { discordPointsApi, useDiscordPointsApi } from "./discordPointsApi";
import { tokenListApi, useTokenListApi } from "./tokenListApi";
import { onboardingApi } from "./onboardingApi";
import { waitlistApi } from "./waitlistApi";
import { externalYieldApi } from "./externalYieldApi";
import { loopApi, useLoopApi } from "./loopApi";
import { jupiterApi } from "./jupiterApi";
import { orcaExternalApi, useOrcaExternalApi } from "./orcaExternalApi";
import { loopPublicApi, useLoopPublicApi } from "./loopPublicApi";
import { marketsPublicApi, useMarketsPublicApi } from "./marketsPublicApi";
import { lendingVaultApi, useLendingVaultApi } from "./lendingVaultsApi";
import { marketsApi, useMarketsApi } from "./marketsApi";
import { loopIxsApi, useLoopIxsApi } from "./loopIxsApi";
import { lendingVaultsPublicApi, useLendingVaultsPublicApi } from "./lendingVaultsPublicApi";
import { earnVaultsPublicApi, useEarnVaultsPublicApi } from "./earnVaultsPublicApi";
import { earnVaultApi, useEarnVaultApi } from "./earnVaultApi";
import { vaultDisclosuresPublicApi, useVaultDisclosuresPublicApi } from "./vaultDisclosuresPublicApi";
import { pointsPublicApi, usePointsPublicApi } from "./pointsPublicApi";
import { geoApi, useGeoApi } from "./geoApi";
import { raydiumExternalApi, useRaydiumExternalApi } from "./raydiumExternalApi";
import { ecosystemApi, useEcosystemApi } from "./ecosystemApi";
import { defiLlamaExternalApi, useDefiLlamaExternalApi } from "./defiLlamaExternalApi";
import { exponentExternalApi, useExponentExternalApi } from "./exponentExternalApi";
import { communityMultiplierApi, useCommunityMultiplierApi } from "./communityMultiplierApi";
import { AbfSdkCacheReducers } from "../cache";
import { rpcApi } from "./rpcApi";
import { useUserTokens } from "../api";
import { timelocksApi, useTimelocksApi } from "./timelocks";
import { lockboxApi, useLockboxApi } from "./lockboxApi";
import { userApi, useUserApi } from "./userApi";

const AbfSdkReducers = {
    authSlice,
    ...AbfSdkCacheReducers,
    [tokenListApi.reducerPath]: tokenListApi.reducer,
    [transactionsApi.reducerPath]: transactionsApi.reducer,
    [marketsPublicApi.reducerPath]: marketsPublicApi.reducer,
    [marketsApi.reducerPath]: marketsApi.reducer,
    [oraclePricesApi.reducerPath]: oraclePricesApi.reducer,
    [pointsApi.reducerPath]: pointsApi.reducer,
    [pointsPublicApi.reducerPath]: pointsPublicApi.reducer,
    [twitterPointsApi.reducerPath]: twitterPointsApi.reducer,
    [geoApi.reducerPath]: geoApi.reducer,
    [discordPointsApi.reducerPath]: discordPointsApi.reducer,
    [onboardingApi.reducerPath]: onboardingApi.reducer,
    [waitlistApi.reducerPath]: waitlistApi.reducer,
    [externalYieldApi.reducerPath]: externalYieldApi.reducer,
    [loopApi.reducerPath]: loopApi.reducer,
    [loopPublicApi.reducerPath]: loopPublicApi.reducer,
    [jupiterApi.reducerPath]: jupiterApi.reducer,
    [orcaExternalApi.reducerPath]: orcaExternalApi.reducer,
    [lendingVaultApi.reducerPath]: lendingVaultApi.reducer,
    [loopIxsApi.reducerPath]: loopIxsApi.reducer,
    [lendingVaultsPublicApi.reducerPath]: lendingVaultsPublicApi.reducer,
    [earnVaultsPublicApi.reducerPath]: earnVaultsPublicApi.reducer,
    [earnVaultApi.reducerPath]: earnVaultApi.reducer,
    [vaultDisclosuresPublicApi.reducerPath]: vaultDisclosuresPublicApi.reducer,
    [raydiumExternalApi.reducerPath]: raydiumExternalApi.reducer,
    [ecosystemApi.reducerPath]: ecosystemApi.reducer,
    [defiLlamaExternalApi.reducerPath]: defiLlamaExternalApi.reducer,
    [exponentExternalApi.reducerPath]: exponentExternalApi.reducer,
    [communityMultiplierApi.reducerPath]: communityMultiplierApi.reducer,
    [rpcApi.reducerPath]: rpcApi.reducer,
    [timelocksApi.reducerPath]: timelocksApi.reducer,
    [lockboxApi.reducerPath]: lockboxApi.reducer,
    [userApi.reducerPath]: userApi.reducer
};

export type SdkRootState = ReturnType<ReturnType<typeof combineReducers<typeof AbfSdkReducers>>>;

const AbfSdkMiddleware: Middleware[] = [
    tokenListApi.middleware,
    transactionsApi.middleware,
    marketsPublicApi.middleware,
    externalYieldApi.middleware,
    oraclePricesApi.middleware,
    marketsApi.middleware,
    pointsApi.middleware,
    pointsPublicApi.middleware,
    twitterPointsApi.middleware,
    discordPointsApi.middleware,
    onboardingApi.middleware,
    waitlistApi.middleware,
    geoApi.middleware,
    loopApi.middleware,
    loopPublicApi.middleware,
    jupiterApi.middleware,
    orcaExternalApi.middleware,
    lendingVaultApi.middleware,
    loopIxsApi.middleware,
    lendingVaultsPublicApi.middleware,
    earnVaultsPublicApi.middleware,
    earnVaultApi.middleware,
    vaultDisclosuresPublicApi.middleware,
    raydiumExternalApi.middleware,
    ecosystemApi.middleware,
    defiLlamaExternalApi.middleware,
    exponentExternalApi.middleware,
    communityMultiplierApi.middleware,
    rpcApi.middleware,
    timelocksApi.middleware,
    lockboxApi.middleware,
    userApi.middleware
];

export function useAbfFetches() {
    const marketsPublic = useMarketsPublicApi();
    const oracle = useOraclePricesApi();
    const markets = useMarketsApi();
    const twitterPoints = useTwitterPointsApi();
    const discordPoints = useDiscordPointsApi();
    const points = usePointsApi();
    const pointsPublic = usePointsPublicApi();
    const loop = useLoopApi();
    const geo = useGeoApi();
    const loopPublic = useLoopPublicApi();
    const orcaExternal = useOrcaExternalApi();
    const lendingVault = useLendingVaultApi();
    const lendingVaultPublic = useLendingVaultsPublicApi();
    const earnVaultPublic = useEarnVaultsPublicApi();
    const earnVault = useEarnVaultApi();
    const vaultDisclosuresPublic = useVaultDisclosuresPublicApi();
    const tokenList = useTokenListApi();
    const loopIxs = useLoopIxsApi();
    const raydiumExternal = useRaydiumExternalApi();
    const ecosystem = useEcosystemApi();
    const defiLlama = useDefiLlamaExternalApi();
    const exponentExternal = useExponentExternalApi();
    const communityMultiplier = useCommunityMultiplierApi();
    const timelocks = useTimelocksApi();
    const lockbox = useLockboxApi();
    const user = useUserApi();
    const all = {
        ...marketsPublic,
        ...oracle,
        ...twitterPoints,
        ...discordPoints,
        ...points,
        ...pointsPublic,
        ...loop,
        ...geo,
        ...loopPublic,
        ...orcaExternal,
        ...lendingVault,
        ...markets,
        ...tokenList,
        ...loopIxs,
        ...lendingVaultPublic,
        ...earnVaultPublic,
        ...earnVault,
        ...vaultDisclosuresPublic,
        ...raydiumExternal,
        ...ecosystem,
        ...defiLlama,
        ...exponentExternal,
        ...communityMultiplier,
        ...timelocks,
        ...lockbox,
        ...user
    };

    function resetAll() {
        all.resetLoanApi();
        all.resetNapoleonApi();
        all.resetMarketsPublicApi();
        all.resetOraclePricesApi();
        all.resetTwitterInfo();
        all.resetDiscordInfo();
        all.resetPointsAndReferrals();
        all.resetLoanMultipliers();
        all.resetLoopApi();
        all.resetGeoApi();
        all.resetLoopApiPublic();
        all.resetOrcaExternalApi();
        all.resetLendingVaultPositionsApi();
        all.resetLendingVaultAccessApi();
        all.resetNapoleonApi();
        all.resetRewardsApi();
        all.resetTokenList();
        all.resetClmmPositions();
        all.resetLoopIxsApi();
        all.resetLendingVaultPublicApi();
        all.resetEarnVaultPublicApi();
        all.resetEarnVaultPositionsApi();
        all.resetVaultDisclosuresPublicApi();
        all.resetRaydiumExternalApi();
        all.resetEcosystemsApi();
        all.resetDefiLlamaExternalApi();
        all.resetExponentExternalApi();
        all.resetCommunityMultipliers();
        all.resetTimelocksApi();
        all.resetLockboxApi();
        all.resetLockboxAccessApi();
        all.resetUserApi();
    }

    return { resetAll, ...all };
}

/**
 * Centralized hook to reset all user-specific wallet data.
 * Use this when wallet connects, disconnects, or switches.
 */
export function useResetAllWalletData() {
    const { refresh } = useUserTokens();
    const {
        resetLoopApi,
        resetRewardsApi,
        resetLendingVaultPositionsApi,
        resetLendingVaultAccessApi,
        resetEarnVaultPositionsApi,
        resetPointsAndReferrals,
        resetLockboxApi,
        resetLockboxAccessApi,
        resetUserApi
    } = useAbfFetches();

    return useCallback(() => {
        void refresh();
        resetLoopApi();
        resetRewardsApi();
        resetLendingVaultPositionsApi();
        resetLendingVaultAccessApi();
        resetEarnVaultPositionsApi();
        resetPointsAndReferrals();
        resetLockboxApi();
        resetLockboxAccessApi();
        resetUserApi();
    }, [
        refresh,
        resetLoopApi,
        resetRewardsApi,
        resetLendingVaultPositionsApi,
        resetLendingVaultAccessApi,
        resetEarnVaultPositionsApi,
        resetPointsAndReferrals,
        resetLockboxApi,
        resetLockboxAccessApi,
        resetUserApi
    ]);
}

export * from "./authSlice";
export * from "./tokenListApi";
export * from "./transactionsApi";
export * from "./marketsPublicApi";
export * from "./oraclePricesApi";
export * from "./pointsApi";
export * from "./pointsPublicApi";
export * from "./twitterPointsApi";
export * from "./discordPointsApi";
export * from "./onboardingApi";
export * from "./waitlistApi";
export * from "./externalYieldApi";
export * from "./loopApi";
export * from "./geoApi";
export * from "./loopPublicApi";
export * from "./jupiterApi";
export * from "./orcaExternalApi";
export * from "./lendingVaultsApi";
export * from "./marketsApi";
export * from "./loopIxsApi";
export * from "./lendingVaultsPublicApi";
export * from "./earnVaultsPublicApi";
export * from "./earnVaultApi";
export * from "./vaultDisclosuresPublicApi";
export * from "./raydiumExternalApi";
export * from "./ecosystemApi";
export * from "./defiLlamaExternalApi";
export * from "./exponentExternalApi";
export * from "./communityMultiplierApi";
export * from "../cache";
export * from "./rpcApi";
export * from "./timelocks";
export * from "./lockboxApi";
export * from "./userApi";
export * from "./type-util";
export { AbfSdkMiddleware, AbfSdkReducers };
